import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ScorecardView from '../components/rounds/ScorecardView';
import { playersAPI, scoresAPI, roundsAPI } from '../services/api';
import { hasBowl } from '../utils/eventTypes';

function ChevronLeft() {
  return (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
    </svg>
  );
}

export default function PlayerDetail() {
  const { playerId, roundId } = useParams();
  const navigate = useNavigate();

  const [player, setPlayer] = useState(null);
  const [eventRounds, setEventRounds] = useState([]);
  const [currentPosition, setCurrentPosition] = useState(null);
  const [championshipPoints, setChampionshipPoints] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (roundId && playerId) {
      fetchEventData();
    }
  }, [playerId, roundId]);

  const fetchEventData = async () => {
    try {
      setLoading(true);

      const playerData = await playersAPI.getById(playerId);
      const player = playerData.player || playerData;
      setPlayer(player);

      const currentRound = await roundsAPI.getById(roundId);
      const eventName = currentRound.event_name;
      const eventType = currentRound.event_type;

      // Position from leaderboard
      const leaderboard = await roundsAPI.getLeaderboard(roundId);
      const lb = Array.isArray(leaderboard) ? leaderboard : [];
      const lbEntry = lb.find(p => p.player_id === parseInt(playerId));
      if (lbEntry) {
        setCurrentPosition(lb.findIndex(p => p.player_id === parseInt(playerId)) + 1);
      }

      // All rounds for this event — matched by event_name + event_type, NOT
      // course_id. An event's rounds can be played at different courses (e.g.
      // a guest event's two rounds), so course_id would wrongly drop rounds
      // played elsewhere; event_type keeps a same-named TML/guest event apart.
      const allRounds = await roundsAPI.getAll();
      let rounds = eventName
        ? allRounds.filter(r => r.event_name === eventName && r.event_type === eventType)
        : [currentRound];

      // Sort chronologically
      rounds.sort((a, b) => new Date(a.start_date) - new Date(b.start_date));

      // Assign display labels. "Championship" is TML branding — other event
      // types (guest 'open' events, regular) just get "Round N".
      const showChampionshipLabel = hasBowl(eventType);
      let championshipCount = 0;
      const labeled = rounds.map(r => {
        if (r.round_type === 'practice') return { ...r, label: 'Practice Round' };
        championshipCount++;
        return { ...r, label: showChampionshipLabel ? `Championship Round ${championshipCount}` : `Round ${championshipCount}` };
      });

      // Fetch scorecards
      const roundsWithScores = await Promise.all(
        labeled.map(async (round) => {
          const fullRound = round.players ? round : await roundsAPI.getById(round.id);
          const roundPlayer = fullRound.players.find(p => p.player_id === parseInt(playerId));
          if (!roundPlayer) return null;

          const scorecard = await scoresAPI.getScorecard(roundPlayer.round_player_id);
          return {
            round_id: fullRound.id,
            status: fullRound.status,
            round_type: fullRound.round_type,
            label: round.label,
            total_points: roundPlayer.total_points || 0,
            total_strokes: roundPlayer.total_strokes || 0,
            handicap_at_time: roundPlayer.handicap_at_time,
            scorecard,
          };
        })
      );

      const valid = roundsWithScores.filter(Boolean);

      // Only show the scorecard for the live/current round the user tapped
      // through from — not the practice round or other rounds.
      const currentRoundId = parseInt(roundId);
      const displayRounds = valid.filter(r => r.round_id === currentRoundId);
      setEventRounds(displayRounds.length ? displayRounds : valid);

      // Championship points only (excludes practice)
      const champTotal = valid
        .filter(r => r.round_type === 'championship')
        .reduce((sum, r) => sum + (r.total_points || 0), 0);
      setChampionshipPoints(champTotal);
    } catch (err) {
      setError('Failed to load player data: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const getPosSuffix = (pos) => {
    if (!pos) return '';
    const t = pos % 100, l = pos % 10;
    if (t >= 11 && t <= 13) return 'th';
    if (l === 1) return 'st';
    if (l === 2) return 'nd';
    if (l === 3) return 'rd';
    return 'th';
  };

  if (loading) {
    return <div className="flex justify-center py-12"><LoadingSpinner /></div>;
  }

  if (error || !player) {
    return (
      <div className="px-4 pb-4">
        <button onClick={() => navigate(-1)} className="text-gray-600 font-semibold flex items-center gap-1 mb-4">
          <ChevronLeft /> Back
        </button>
        <div className="bg-white rounded-2xl p-8 text-center border border-gray-200">
          <p className="text-gray-500">{error || 'Player not found'}</p>
        </div>
      </div>
    );
  }

  const hasChampionshipRounds = eventRounds.some(r => r.round_type === 'championship');

  return (
    <div className="px-4 pb-8">
      {/* Back */}
      <button onClick={() => navigate(-1)} className="text-gray-600 font-semibold flex items-center gap-1 mb-4">
        <ChevronLeft /> Back
      </button>

      {/* Player header card */}
      <div className="bg-white rounded-2xl p-5 mb-4 border border-gray-200 shadow-sm">
        {/* Name + handicap */}
        <h2 className="text-2xl font-bold text-gray-900 mb-1">
          {player.name}
          {eventRounds[0]?.handicap_at_time !== null && eventRounds[0]?.handicap_at_time !== undefined && (
            <span className="text-lg font-normal text-gray-400 ml-1">({eventRounds[0].handicap_at_time})</span>
          )}
        </h2>

        {/* Points + position */}
        <div className="flex items-baseline gap-3">
          <span className="text-4xl font-bold text-gray-900">
            {hasChampionshipRounds ? championshipPoints : (eventRounds[0]?.total_points || 0)}
          </span>
          <span className="text-gray-400 text-sm">pts</span>
          {currentPosition && (
            <span className="text-gray-600 font-bold text-lg ml-auto">
              {currentPosition}{getPosSuffix(currentPosition)}
            </span>
          )}
        </div>

        {/* Bio */}
        {player.bio && (
          <p className="text-sm text-gray-500 mt-3 leading-relaxed">{player.bio}</p>
        )}
      </div>

      {/* Scorecards — one per round */}
      {eventRounds.length === 0 ? (
        <div className="bg-white rounded-2xl p-6 text-center border border-gray-200">
          <p className="text-gray-400 text-sm">No scorecards available yet</p>
        </div>
      ) : (
        <div className="space-y-4">
          {eventRounds.map((r) => (
            <div key={r.round_id} className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
              {/* Round label bar */}
              <div className="px-4 py-2.5 border-b border-gray-100 flex items-center justify-between">
                <span className="text-sm font-bold text-gray-900">{r.label}</span>
                <span className="text-sm font-bold text-red-600">{r.total_points} pts</span>
              </div>
              <ScorecardView
                scorecard={r.scorecard}
                round={{ status: r.status }}
                hideHeader
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
